import { apiClient } from '@/shared/lib/api'
import type {
  Auth47ChallengeResponse,
  AuthResponse,
  ChangePasswordRequest,
  LnurlAuthChallengeResponse,
  LnurlLinkStatusResponse,
  LnurlStatusRequest,
  PublicKeyAuthAction,
  PublicKeyAuthStatusResponse,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
} from './types'
import { appendReferralCodeQuery } from './lib/referral'

export async function createLnurlAuthChallenge(
  action: Extract<PublicKeyAuthAction, 'login' | 'signup'>,
  options?: { referralCode?: string | null }
): Promise<LnurlAuthChallengeResponse> {
  const endpointAction = action === 'signup' ? 'register' : 'login'
  const endpoint = appendReferralCodeQuery(
    `/auth/lnurl/${endpointAction}`,
    action === 'signup' ? options?.referralCode : null
  )
  return apiClient<LnurlAuthChallengeResponse>(endpoint, { skipAuthRefresh: true })
}

export async function pollLnurlAuthStatus(
  request: LnurlStatusRequest
): Promise<PublicKeyAuthStatusResponse> {
  return apiClient<PublicKeyAuthStatusResponse>('/auth/lnurl/status', {
    method: 'POST',
    skipAuthRefresh: true,
    body: JSON.stringify(request),
  })
}

export async function createLnurlLinkChallenge(): Promise<LnurlAuthChallengeResponse> {
  return apiClient<LnurlAuthChallengeResponse>('/auth/lnurl/link')
}

export async function pollLnurlLinkStatus(
  request: LnurlStatusRequest
): Promise<LnurlLinkStatusResponse> {
  return apiClient<LnurlLinkStatusResponse>('/auth/lnurl/link/status', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export async function unlinkLnurlAuth(): Promise<LnurlLinkStatusResponse> {
  return apiClient<LnurlLinkStatusResponse>('/auth/lnurl/unlink', {
    method: 'POST',
  })
}

export async function unlinkAuth47Auth(): Promise<LnurlLinkStatusResponse> {
  return apiClient<LnurlLinkStatusResponse>('/auth/auth47/unlink', {
    method: 'POST',
  })
}

export async function createAuth47Challenge(options?: {
  skipAuthRefresh?: boolean
  referralCode?: string | null
}): Promise<Auth47ChallengeResponse> {
  const endpoint = appendReferralCodeQuery('/auth/auth47/challenge', options?.referralCode)
  return apiClient<Auth47ChallengeResponse>(endpoint, {
    skipAuthRefresh: options?.skipAuthRefresh,
  })
}

export async function pollAuth47Status(uuid: string): Promise<PublicKeyAuthStatusResponse> {
  return apiClient<PublicKeyAuthStatusResponse>(`/auth/auth47/status/${uuid}`, {
    skipAuthRefresh: true,
  })
}

export async function requestPasswordReset(request: RequestPasswordResetRequest): Promise<void> {
  return apiClient<void>('/auth/request-password-reset', {
    method: 'POST',
    skipAuthRefresh: true,
    body: JSON.stringify(request),
  })
}

export async function resetPassword(request: ResetPasswordRequest): Promise<AuthResponse> {
  return apiClient<AuthResponse>('/auth/password-reset', {
    method: 'POST',
    skipAuthRefresh: true,
    body: JSON.stringify(request),
  })
}

export async function changePassword(request: ChangePasswordRequest): Promise<void> {
  return apiClient<void>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}
